import React from 'react'
import {
  Controller,
  useFormContext,
  useFieldArray,
  useWatch,
} from 'react-hook-form'
import { DateTime } from 'luxon'
import CustomLuxonUtils, { buildDateTime } from 'lib/CustomLuxonUtils'
import {
  MuiPickersUtilsProvider,
  DatePicker,
  KeyboardTimePicker,
} from '@material-ui/pickers'
import Grid from '@material-ui/core/Grid'
import InputAdornment from '@material-ui/core/InputAdornment'
import EventIcon from '@material-ui/icons/Event'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Box from '@material-ui/core/Box'
import TimeIcon from '@material-ui/icons/AccessTime'
import { makeStyles } from '@material-ui/core/styles'
import { Typography } from '@material-ui/core'
import { toThreeLetterTimezone } from 'utils/timezones'

const useStyles = makeStyles(theme => ({
  dateTimeRow: {
    marginBottom: '30px',
  },
  divider: {
    margin: '30px 0px',
    borderTop: '1px solid #E0E0E0',
    borderBottom: '0',
  },
  additionalSessionInfo: {
    textAlign: 'center',
    paddingTop: '5px',
  },
  addSessionBtn: {
    marginTop: '15px',
  },
  multiSessionInfoText: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  infoHeader: {
    paddingBottom: '5px',
  },
  adornedEnd: {
    paddingRight: '8px',
    '& button': {
      padding: 0,
    },
  },
}))

const initialFocusedDate = DateTime.local()
  .startOf('day')
  .toJSDate()

const MultiSessionInput = ({ name, keyName = 'id', timezone }) => {
  const classes = useStyles()
  const { control, register, errors } = useFormContext()
  const { append, remove, fields } = useFieldArray({ control, name, keyName })

  const eventSessions = useWatch({ control, name })
  const styles = useStyles()

  const addSession = () => {
    const lastSession = eventSessions.slice(-1)[0]
    const nextDayStart = lastSession.startDate.plus({ days: 1 })
    const nextStartTime = buildDateTime(nextDayStart, lastSession.startTime)
    const nextEndTime = buildDateTime(nextDayStart, lastSession.endTime)

    const session = {
      startDate: nextDayStart,
      startTime: nextStartTime,
      endTime: nextEndTime,
    }

    append(session)
  }

  const handleTimeChange = (event, onChange, startDate) => {
    if (!event) return
    const year = startDate.get('year') || event.get('year')
    const month = startDate.get('month') || event.get('month')
    const day = startDate.get('day') || event.get('day')
    const hour = event.get('hour')
    const minute = event.get('minute')
    const roundedMinutesTime = DateTime.local(
      year,
      month,
      day,
      hour,
      Math.floor(minute / 5) * 5,
    )
    onChange(roundedMinutesTime)
  }

  const defineHelperText = (errors, name, index) => {
    if (!errors[name]?.[index]) return
    if (errors[name][index]?.startTime) {
      return errors[name][index].startTime.message
    } else if (errors[name][index]?.endTime) {
      return errors[name][index].endTime.message
    } else if (errors[name][index]?.message) {
      return errors[name][index].message
    }
  }

  const defineTimezone = (index, timezone, field) => {
    const resultTimezone = toThreeLetterTimezone(
      timezone,
      buildDateTime(
        eventSessions[index]?.startDate,
        eventSessions[index]?.[field],
      ),
    )
    return resultTimezone ? `(${resultTimezone})` : ''
  }

  return (
    <MuiPickersUtilsProvider utils={CustomLuxonUtils}>
      <Box mb={2} mt={2}>
        {fields.map((session, index) => {
          return (
            <Grid
              className={classes.dateTimeRow}
              key={session.id}
              container
              spacing={1}
              alignItems={errors?.sessions?.[index] ? 'flex-start' : 'center'}
            >
              <Grid item xs={fields.length > 1 ? 5 : 6}>
                <Controller
                  control={control}
                  name={`${name}[${index}].startDate`}
                  defaultValue={session.startDate}
                  render={({ onChange, value }) => (
                    <DatePicker
                      label="Date"
                      id="start-date"
                      name={`${name}[${index}].startDate`}
                      initialFocusedDate={initialFocusedDate}
                      innerRef={register}
                      value={value}
                      error={!!errors[name]?.[index]}
                      helperText={
                        errors[name]?.[index]?.startDate
                          ? 'Please select a start date'
                          : null
                      }
                      format="MMM dd, yyyy"
                      onChange={onChange}
                      disablePast
                      InputProps={{
                        endAdornment: (
                          <InputAdornment>
                            <EventIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`${name}[${index}].startTime`}
                  defaultValue={session.startTime}
                  render={({ onChange, value }) => (
                    <KeyboardTimePicker
                      label={`Start time ${defineTimezone(
                        index,
                        timezone,
                        'startTime',
                      )}`}
                      id="start-time"
                      name={`${name}[${index}].startTime`}
                      initialFocusedDate={initialFocusedDate}
                      minutesStep={5}
                      mask="__:__ _M"
                      keyboardIcon={<TimeIcon />}
                      value={value}
                      innerRef={register}
                      error={!!errors[name]?.[index]}
                      helperText={defineHelperText(errors, name, index)}
                      onChange={e =>
                        handleTimeChange(e, onChange, session.startDate)
                      }
                      InputProps={{
                        classes: {
                          adornedEnd: styles.adornedEnd,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`${name}[${index}].endTime`}
                  defaultValue={session.endTime}
                  render={({ onChange, value }) => (
                    <KeyboardTimePicker
                      label={`End time ${defineTimezone(
                        index,
                        timezone,
                        'endTime',
                      )}`}
                      id="end-time"
                      name={`${name}[${index}].endTime`}
                      initialFocusedDate={initialFocusedDate}
                      minutesStep={5}
                      mask="__:__ _M"
                      keyboardIcon={<TimeIcon />}
                      value={value}
                      innerRef={register}
                      error={!!errors[name]?.[index]}
                      helperText={defineHelperText(errors, name, index)}
                      onChange={e =>
                        handleTimeChange(e, onChange, session.startDate)
                      }
                      InputProps={{
                        classes: {
                          adornedEnd: styles.adornedEnd,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                {fields.length > 1 && (
                  <IconButton
                    onClick={() => {
                      remove(index)
                    }}
                  >
                    <DeleteOutlineIcon color="primary" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          )
        })}
        <hr className={classes.divider} />
        <Box className={classes.additionalSessionInfo}>
          {eventSessions.length <= 1 && (
            <>
              <Typography
                variant="h6"
                color="primary"
                className={classes.infoHeader}
              >
                Multiple Sessions
              </Typography>
              <Typography
                variant="body1"
                className={classes.multiSessionInfoText}
              >
                Adding a session will create a single offering with multiple
                sessions.
              </Typography>
              <Typography
                variant="body1"
                className={classes.multiSessionInfoText}
              >
                Participants enrolling in this offering will be registered for{' '}
                <strong>all sessions</strong>.
              </Typography>
            </>
          )}
          <Button
            aria-controls="add-session-time"
            aria-haspopup="true"
            color="primary"
            variant="outlined"
            size="large"
            onClick={addSession}
            alignItems="center"
            className={classes.addSessionBtn}
          >
            {'+ Add Session'}
          </Button>
        </Box>
      </Box>
    </MuiPickersUtilsProvider>
  )
}

export default MultiSessionInput
